import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Check, Circle, Loader2, LockKeyhole } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FormAlert } from '@/components/ui/form-alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { genericErrorMessage, httpStatus, serverCode, serverDetails } from '@/lib/api-errors';
import { authApi } from '@/lib/auth-api';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';

// Miroir de validatePlatformPassword (@logistech/platform-auth, non importable côté navigateur : bcrypt).
// Le serveur reste juge : PASSWORD_POLICY_VIOLATION si les deux divergent.
export const PASSWORD_RULES: { label: string; test: (p: string) => boolean }[] = [
  { label: '12 caractères minimum', test: (p) => p.length >= 12 },
  { label: 'Une majuscule', test: (p) => /[A-Z]/.test(p) },
  { label: 'Un chiffre', test: (p) => /\d/.test(p) },
  { label: 'Un caractère spécial', test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Mot de passe actuel requis'),
    newPassword: z.string().superRefine((p, ctx) => {
      const missing = PASSWORD_RULES.filter((r) => !r.test(p)).map((r) => r.label.toLowerCase());
      if (missing.length) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Manque : ${missing.join(', ')}` });
    }),
    confirmNewPassword: z.string(),
    // TOTP 6 chiffres ou code de secours XXXX-XXXX (mêmes bornes que platform-api)
    mfaCode: z.string().trim().min(6, 'Code à 6 chiffres ou code de secours').max(9, 'Code à 6 chiffres ou code de secours'),
  })
  .refine((f) => f.confirmNewPassword === f.newPassword, { path: ['confirmNewPassword'], message: 'Les deux saisies diffèrent' });

export type ChangePasswordForm = z.infer<typeof changePasswordSchema>;

export const RATE_LIMITED_MESSAGE = 'Trop de tentatives, réessayez dans 15 min';

const Field = ({ id, label, error, children }: { id: string; label: string; error?: string; children: ReactNode }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    {children}
    {error && (
      <p id={`${id}-error`} className="text-xs text-danger">
        {error}
      </p>
    )}
  </div>
);

export const ChangePasswordCard = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<ChangePasswordForm>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmNewPassword: '', mfaCode: '' },
  });
  const newPassword = watch('newPassword');

  const mutation = useMutation({
    mutationFn: authApi.changePassword,
    // Re-login forcé : le jeton courant reste valide côté serveur jusqu'à expiration (TD-031), on le jette ici
    onSuccess: async () => {
      toast.success('Mot de passe mis à jour, reconnectez-vous');
      clearAuth();
      queryClient.clear();
      await navigate({ to: '/login', replace: true });
    },
    // 401 sans code de KEEP_SESSION_ERROR_CODES : l'intercepteur a déjà renvoyé vers /login
    onError: (err) => {
      const code = serverCode(err);
      if (code === 'INVALID_CURRENT_PASSWORD') setError('currentPassword', { message: 'Mot de passe actuel incorrect' }, { shouldFocus: true });
      else if (code === 'INVALID_MFA_CODE') setError('mfaCode', { message: 'Code MFA invalide' }, { shouldFocus: true });
      else if (code === 'SAME_PASSWORD') {
        setError('newPassword', { message: 'Le nouveau mot de passe doit être différent de l’actuel' }, { shouldFocus: true });
      } else if (code === 'PASSWORD_POLICY_VIOLATION') {
        const violations = (serverDetails(err) as { violations?: string[] } | undefined)?.violations ?? [];
        setError('newPassword', { message: violations.join(', ') || 'Politique de mot de passe non respectée' }, { shouldFocus: true });
      } else if (httpStatus(err) === 429) toast.error(RATE_LIMITED_MESSAGE);
      else setFormError(genericErrorMessage(err));
    },
  });

  const onSubmit = ({ currentPassword, newPassword: next, mfaCode }: ChangePasswordForm) => {
    setFormError(null);
    mutation.mutate({ currentPassword, newPassword: next, mfaCode: mfaCode.trim() });
  };

  const aria = (name: keyof ChangePasswordForm) => ({
    'aria-invalid': Boolean(errors[name]),
    'aria-describedby': errors[name] ? `${name}-error` : undefined,
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <LockKeyhole className="h-4 w-4 text-brand-violet" aria-hidden />
          Changer le mot de passe
        </CardTitle>
        <CardDescription>Mot de passe actuel et code MFA exigés. Vous serez déconnecté après le changement.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4" aria-label="Changer le mot de passe">
          <Field id="currentPassword" label="Mot de passe actuel" error={errors.currentPassword?.message}>
            <Input id="currentPassword" type="password" autoComplete="current-password" {...register('currentPassword')} {...aria('currentPassword')} />
          </Field>
          <Field id="newPassword" label="Nouveau mot de passe" error={errors.newPassword?.message}>
            <Input id="newPassword" type="password" autoComplete="new-password" {...register('newPassword')} {...aria('newPassword')} />
          </Field>
          <ul className="grid gap-1 text-xs sm:grid-cols-2" data-testid="password-policy" aria-label="Politique de mot de passe">
            {PASSWORD_RULES.map((rule) => {
              const ok = rule.test(newPassword);
              return (
                <li key={rule.label} className={cn('flex items-center gap-1.5', ok ? 'text-success' : 'text-muted-foreground')} data-ok={ok}>
                  {ok ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Circle className="h-3.5 w-3.5" aria-hidden />}
                  {rule.label}
                </li>
              );
            })}
          </ul>
          <Field id="confirmNewPassword" label="Confirmer le nouveau mot de passe" error={errors.confirmNewPassword?.message}>
            <Input
              id="confirmNewPassword"
              type="password"
              autoComplete="new-password"
              {...register('confirmNewPassword')}
              {...aria('confirmNewPassword')}
            />
          </Field>
          <Field id="mfaCode" label="Code MFA (application TOTP ou code de secours)" error={errors.mfaCode?.message}>
            <Input id="mfaCode" autoComplete="one-time-code" maxLength={9} className="font-mono sm:w-48" {...register('mfaCode')} {...aria('mfaCode')} />
          </Field>

          {formError && <FormAlert>{formError}</FormAlert>}
          <div className="flex justify-end">
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
              {mutation.isPending ? 'Mise à jour…' : 'Changer le mot de passe'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
